/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSkillTagsRequest,
  ListSkillTagsSecurity,
} from "../models/operations/listskilltags.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillTagsQuery,
  prefetchSkillTags,
  queryKeySkillTags,
  SkillTagsQueryData,
} from "./skillTags.core.js";
export {
  buildSkillTagsQuery,
  prefetchSkillTags,
  queryKeySkillTags,
  type SkillTagsQueryData,
};

export type SkillTagsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listTags skills
 *
 * @remarks
 * List distinct tags used by active skills in the project. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillTags(
  request?: ListSkillTagsRequest | undefined,
  security?: ListSkillTagsSecurity | undefined,
  options?: QueryHookOptions<SkillTagsQueryData, SkillTagsQueryError>,
): UseQueryResult<SkillTagsQueryData, SkillTagsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillTagsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listTags skills
 *
 * @remarks
 * List distinct tags used by active skills in the project. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillTagsSuspense(
  request?: ListSkillTagsRequest | undefined,
  security?: ListSkillTagsSecurity | undefined,
  options?: SuspenseQueryHookOptions<SkillTagsQueryData, SkillTagsQueryError>,
): UseSuspenseQueryResult<SkillTagsQueryData, SkillTagsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillTagsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSkillTagsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillTagsQueryData,
): SkillTagsQueryData | undefined {
  const key = queryKeySkillTags(...queryKeyBase);

  return client.setQueryData<SkillTagsQueryData>(key, data);
}

export function invalidateSkillTags(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listTags", ...queryKeyBase],
  });
}

export function invalidateAllSkillTags(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listTags"],
  });
}
